import { ExternalLink } from "lucide-react";
import { getActingTenantId, getRole } from "../auth/session";
import { useMyUsage } from "../api/companies";
import {
  useDashboard,
  type DeviceStatusCounts,
  type FirmwareBucket,
  type TaskOverview as TaskOverviewData,
} from "../api/dashboard";
import { DonutChart, type DonutSegment } from "../components/DonutChart";
import { UsageBar } from "../components/UsageBar";
import { colors } from "../styles/tokens";

function DeviceStatusChart({ counts, total: deviceTotal }: { counts?: DeviceStatusCounts; total?: number }) {
  const data: DonutSegment[] = [
    { name: "Online", value: counts?.online ?? 0, color: colors.status.online },
    // Stale slots between Online and Offline — devices that missed
    // their expected check-in cadence but haven't crossed the offline
    // threshold yet. Server-derived from lastSeen + checkInMinutes.
    { name: "Stale", value: counts?.stale ?? 0, color: colors.status.stale },
    { name: "Offline", value: counts?.offline ?? 0, color: colors.status.offline },
    { name: "Inactive", value: counts?.inactive ?? 0, color: colors.status.inactive },
    { name: "Updating", value: counts?.updating ?? 0, color: colors.status.updating },
    { name: "Error", value: counts?.error ?? 0, color: colors.status.error },
  ];
  const total = deviceTotal ?? data.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 p-5">
      <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">Device Status</h3>
      <div className="flex items-center gap-6">
        <div className="relative h-32 w-32">
          <DonutChart data={data} />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-bold text-gray-900 dark:text-white">{total}</span>
            <span className="text-[10px] text-gray-400 dark:text-riot-navy-400">Devices</span>
          </div>
        </div>
        <div className="space-y-2 text-xs">
          {data.map((d) => (
            <div key={d.name} className="flex items-center gap-2">
              {/* Legend dot — colour is data-driven, set via a ref
                  callback (CSSOM mutation) so the rendered HTML carries
                  no `style="..."` attribute. CSP `style-src 'self'`. */}
              <span
                ref={(el) => {
                  if (el) el.style.backgroundColor = d.color;
                }}
                className="h-2.5 w-2.5 rounded-sm"
              />
              <span className="text-gray-500 dark:text-riot-navy-300">{d.value}</span>
              <span className="text-gray-700 dark:text-riot-navy-200">{d.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, info, period }: { title: string; value: string | number; info?: boolean; period?: boolean }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 p-5">
      <div className="mb-2 flex items-center gap-1.5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        {info && (
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-riot-gold-100 text-[9px] font-bold text-riot-gold-500">i</span>
        )}
      </div>
      <p className="text-3xl font-bold text-gray-900 dark:text-white">{value}</p>
      {period && (
        <p className="mt-2 text-xs text-gray-400 dark:text-riot-navy-400">Period 30 days</p>
      )}
    </div>
  );
}

function TaskOverview({ overview }: { overview?: TaskOverviewData }) {
  const cells: Array<{ label: string; value: number }> = [
    { label: "Scheduled", value: overview?.scheduled ?? 0 },
    { label: "Queued", value: overview?.queued ?? 0 },
    { label: "In progress", value: overview?.inProgress ?? 0 },
    { label: "Paused", value: overview?.paused ?? 0 },
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 p-5">
      <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Tasks</h3>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {cells.map((c) => (
          <div key={c.label}>
            <p className="text-xs text-gray-400 dark:text-riot-navy-400">{c.label}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{c.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlatformScopeCard() {
  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 p-5">
      <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Scope</h3>
      <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2.5 dark:border-riot-navy-700 dark:bg-riot-navy-900/40">
        <span className="text-lg font-bold text-gray-900 dark:text-white">All companies</span>
        <p className="text-[11px] text-gray-400 dark:text-riot-navy-400">Platform-wide totals</p>
      </div>
    </div>
  );
}

function CompanyLevelCard() {
  const { data: usage } = useMyUsage();
  const u = usage?.data;

  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 p-5">
      <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Company level</h3>
      <div className="mb-4 rounded-md border border-gray-100 bg-gray-50 px-3 py-2.5 dark:border-riot-navy-700 dark:bg-riot-navy-900/40">
        <div className="flex items-center gap-1.5">
          <span className="text-lg font-bold capitalize text-gray-900 dark:text-white">{u?.subscriptionTier ?? "Basic"}</span>
          <ExternalLink size={14} className="text-gray-400 dark:text-riot-navy-400" />
        </div>
        <p className="text-[11px] text-gray-400 dark:text-riot-navy-400">Essentials for remote device update</p>
      </div>
      <p className="mb-2 text-xs font-semibold text-gray-700 dark:text-riot-navy-200">Resource limits</p>
      {u && (
        <div className="space-y-2.5">
          <UsageBar label="Devices" current={u.devices.current} limit={u.devices.limit} />
          <UsageBar label="Users" current={u.users.current} limit={u.users.limit} />
          <UsageBar label="Configuration files" current={u.firmware.current} limit={u.firmware.limit} />
        </div>
      )}
    </div>
  );
}

function FirmwareDistribution({ versions }: { versions?: FirmwareBucket[] }) {
  const rows = versions ?? [];
  const total = rows.reduce((sum, v) => sum + v.count, 0);

  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 p-5">
      <div className="mb-3 flex items-center gap-1.5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Firmware versions</h3>
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-riot-gold-100 text-[9px] font-bold text-riot-gold-500">i</span>
      </div>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-xs text-gray-400 dark:text-riot-navy-400">No firmware data yet</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => {
            const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
            return (
              <div key={row.version} className="flex items-center gap-3 text-xs">
                <span className="w-24 truncate text-gray-700 dark:text-riot-navy-200">{row.version}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-riot-navy-700">
                  {/* Width is a runtime percentage; setProperty via ref
                      callback keeps the DOM free of inline style="..."
                      so CSP `style-src 'self'` holds. */}
                  <div
                    ref={(el) => {
                      if (el) el.style.setProperty("--w", `${pct}%`);
                    }}
                    className="h-full rounded-full bg-riot-gold-500 [width:var(--w)]"
                  />
                </div>
                <span className="w-10 text-right text-gray-500 dark:text-riot-navy-300">{row.count}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ConfigDistribution() {
  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 p-5">
      <div className="mb-3 flex items-center gap-1.5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Configurations</h3>
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-riot-gold-100 text-[9px] font-bold text-riot-gold-500">i</span>
      </div>
      <p className="py-6 text-center text-xs text-gray-400 dark:text-riot-navy-400">No configuration data yet</p>
    </div>
  );
}

export function DashboardPage() {
  const { data, isLoading, error } = useDashboard();
  const isPlatformDashboard = getRole() === "super_admin" && !getActingTenantId();
  // Device status counts come from the server's dashboard, which counts by
  // the same freshness rule the device list shows. Walking every device in
  // the browser to recount them doesn't scale past a small fleet.
  const snapshot = data?.data;
  const deviceStatus = snapshot?.deviceStatus;
  const isStatusLoading = isLoading;
  // The server's total is exact even when the online/stale/offline split
  // covers only part of a large fleet; older builds don't send it.
  const imported = deviceStatus
    ? snapshot?.deviceTotal ??
      deviceStatus.online + (deviceStatus.stale ?? 0) + deviceStatus.offline +
        deviceStatus.updating + deviceStatus.error + deviceStatus.inactive
    : 0;
  const activated = deviceStatus ? imported - deviceStatus.inactive : 0;

  return (
    <div>
      <h1 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Dashboard</h1>

      {error && (
        <p className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
          Failed to load dashboard: {String((error as Error).message ?? error)}
        </p>
      )}

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-4">
        <DeviceStatusChart counts={deviceStatus} total={snapshot?.deviceTotal} />
        <StatCard title="Imported devices" value={isStatusLoading ? "…" : imported} info period />
        <StatCard title="Activated devices" value={isStatusLoading ? "…" : activated} info period />
        {isPlatformDashboard ? <PlatformScopeCard /> : <CompanyLevelCard />}
      </div>

      {snapshot?.exact === false && (
        <p className="mb-4 rounded-md border border-riot-gold-200 bg-riot-gold-50 p-3 text-xs text-gray-700 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-riot-navy-200">
          Some figures are minimums. Online, stale and offline come from each device&apos;s last check-in, and
          this fleet is too large to check every device on each load.
        </p>
      )}

      <div className="mb-4">
        <TaskOverview overview={snapshot?.taskOverview} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <FirmwareDistribution versions={snapshot?.firmwareVersions} />
        <ConfigDistribution />
      </div>
    </div>
  );
}
